import { useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { useGameStore } from "@/state/gameStore";
import { useCurrentMessage, useCurrentRoom, useIsMessageVisible } from "@/state/selectors";
import { useMessageDisplayText } from "@/hooks/useMessageDisplayText";
import { Enum_Message_Type } from "@/utils/graphql/generated/graphql";
import Button from "@/components/form/Button";
import DefaultContentContainer from "@/components/shared/DefaultContainer/DefaultContentContainer";
import { CircularCountdown } from "@/components/screens/PlayingScreen/components/Message/components/CircularTimer";
import clsx from "clsx";
import MemeIcon from "@/components/screens/PlayingScreen/components/MemeIcon";
import { MessageResponse } from "@/types/api";

const TIMER_DURATION = 20;

const messageVariants = {
    initial: { y: "100vh" },
    animate: {
        y: 0,
        transition: { duration: 0.5, ease: [0.34, 1.56, 0.64, 1] },
        /*transition: { type: "spring", duration: 0.5, bounce: 0.3 },*/
    },
    exit: {
        y: "100vh",
        transition: { duration: 0.2, ease: "easeInOut" },
    },
} as const;

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function Message() {
    const message = useCurrentMessage();
    const displayText = useMessageDisplayText(message);
    const isVisible = useIsMessageVisible();
    const hasPendingResponse = useGameStore((state) => state.pendingResponse !== null);
    const currentRoom = useCurrentRoom();

    const setPendingResponse = useGameStore((state) => state.setPendingResponse);
    const processResponse = useGameStore((state) => state.processResponse);

    const handleButtonClick = useCallback(
        (response: MessageResponse) => {
            if (hasPendingResponse) return;
            let responseData = response;
            if (message?.type === Enum_Message_Type.PostRoomTransition && currentRoom && currentRoom.rent) {
                responseData = { ...response, cash: currentRoom.rent * -1 };
            }
            // add timeout to make the user feel the button press before the next message appears
            setTimeout(() => setPendingResponse(responseData), 100);
        },
        [hasPendingResponse, setPendingResponse, message, currentRoom],
    );

    const handleExitComplete = useCallback(() => {
        processResponse();
    }, [processResponse]);

    const handleTimerComplete = useCallback(() => {
        const state = useGameStore.getState();

        if (state.currentMessage?.disable_timer === true) return;

        // Read fresh state — don't rely on React render cycle
        if (
            state.pendingResponse !== null ||
            state.queueTimerActive ||
            state.currentPostMessageAnimationStep !== null ||
            state.currentMessage === null
        ) {
            return;
        }

        const autoResponse = state.currentMessage.Response?.find(
            (response): response is NonNullable<typeof response> =>
                response !== null && response.timerAutoResponse === true,
        );
        if (autoResponse) {
            setPendingResponse(autoResponse);
        }
    }, [setPendingResponse]);

    return (
        <AnimatePresence mode="wait" onExitComplete={handleExitComplete}>
            {isVisible && message && (
                <motion.div
                    key={message.documentId}
                    variants={messageVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="absolute bottom-16 left-0 right-0 z-10 pb-[env(safe-area-inset-bottom)]"
                >
                    <div className="mt-4 flex flex-row items-center justify-center mx-4">
                        <DefaultContentContainer className="w-full">
                            <div className="flex w-full flex-col items-center justify-between p-4">
                                <div className="flex w-full items-start gap-3">
                                    {message.meme_icon && (
                                        <div>
                                            <MemeIcon memeIcon={message.meme_icon} />
                                        </div>
                                    )}
                                    <div className="flex w-full flex-col items-center justify-between gap-1">
                                        <div className="flex items-center justify-between w-full">
                                            <div className="font-semibold text-white/65">{message.sender}</div>
                                            {!message.disable_timer && (
                                                <CircularCountdown
                                                    key={message.documentId}
                                                    initialSeconds={TIMER_DURATION}
                                                    onComplete={handleTimerComplete}
                                                />
                                            )}
                                        </div>
                                        <div className="leading-tight w-full">{displayText}</div>
                                    </div>
                                </div>

                                <div
                                    className={clsx(
                                        "flex items-center justify-center gap-2 w-full mt-3",
                                        (message.Response?.filter(Boolean).length ?? 0) > 2 ||
                                            message.vertical_button_order
                                            ? "flex-col"
                                            : "flex-row",
                                    )}
                                >
                                    {message.Response?.filter(
                                        (response): response is NonNullable<typeof response> => response !== null,
                                    ).map((response, index) => (
                                        <Button
                                            key={index}
                                            className="shrink-1"
                                            fullWidth={true}
                                            onClick={() => handleButtonClick(response)}
                                            disabled={hasPendingResponse}
                                        >
                                            {response.text}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </DefaultContentContainer>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default Message;